import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight, Bell, BookOpen, BriefcaseBusiness, Compass, Flame,
  Home, LockKeyhole, LogOut, Radio, Search, ShieldCheck, Smartphone, Sparkles, Star, UserRound, X
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useRole } from "@/context/RoleContext";
import { useLanguage } from "@/context/LanguageContext";
import DashboardLayout from "@/components/DashboardLayout";
import EnergyBalanceIndicator from "@/components/EnergyBalanceIndicator";
import SessionActivityTracker from "@/components/SessionActivityTracker";
import { KD9LogoCompact } from "@/components/KD9Logo";
import { mobileCatalog, mobileModeEnabled, mobileTabs, MOBILE_MODE_KEY, isAdministrativeAccount } from "./mobileNavigation";
import DashboardRouter from "@/pages/dashboard/DashboardRouter";
import "./mobile-experience.css";

const ICONS = {
  home: Home, compass: Compass, briefcase: BriefcaseBusiness, radio: Radio, user: UserRound,
};
const favorites = [
  { to: "/app/energia-kd", title: "Energía KD9 · OnePay", description: "Consulta saldo y recarga", Icon: Flame },
  { to: "/app/horoscopo", title: "Horóscopo diario", description: "Descubre tus 12 signos", Icon: Star },
  { to: "/app/diagnosticos", title: "Diagnósticos", description: "Inicia tu estudio", Icon: Sparkles },
  { to: "/app/academia", title: "Academia", description: "Aprende y avanza", Icon: BookOpen },
  { to: "/app/tienda", title: "Marketplace", description: "Descubre productos", Icon: Compass },
  { to: "/app/oraculo", title: "Oráculo", description: "Explora tus preguntas", Icon: Flame },
  { to: "/app/bolsa-diagnosticos", title: "Bolsa de diagnósticos", description: "Servicios disponibles", Icon: BriefcaseBusiness },
  { to: "/app/mis-trabajos", title: "Mis trabajos", description: "Gestiona entregas", Icon: ShieldCheck },
  { to: "/app/mis-productos", title: "Mis productos", description: "Gestiona tu catálogo", Icon: BriefcaseBusiness },
  { to: "/app/contenidos-academia", title: "Crear contenidos", description: "Tu espacio de formación", Icon: BookOpen },
];

/** Android TWA entry. The web landing and desktop dashboard remain unmodified. */
export function MobileEntry() {
  const navigate = useNavigate();
  const { ready, user } = useAuth();
  useEffect(() => {
    try { window.sessionStorage.setItem(MOBILE_MODE_KEY, "1"); }
    catch (_) { /* Restricted storage: access control still uses the server map. */ }
    if (!ready) return;
    navigate(user ? "/app/inicio" : "/movil/login", { replace: true });
  }, [navigate, ready, user]);
  return <div className="kd9-mob-enter">KHAOS DELTA 9</div>;
}

/** The same backend routes are rendered below a distinct native-like chrome. */
export function AppShellSwitch() {
  const { accessMap, refresh } = useAuth();
  if (!mobileModeEnabled()) return <DashboardLayout />;
  if (!accessMap?.roles?.length) return (
    <main className="kd9-mob-admin-gate" data-testid="mobile-access-map-unavailable">
      <ShieldCheck size={38} aria-hidden="true"/>
      <h1>Verificación pendiente</h1>
      <p>No pudimos verificar los permisos de tu cuenta. Por seguridad, el menú móvil no se mostrará hasta validarlos.</p>
      <button type="button" onClick={() => refresh()}>Reintentar verificación</button>
    </main>
  );
  if (isAdministrativeAccount(accessMap)) {
    return accessMap.roles.includes("director") ? <MobileAdminGate /> : <MobilePaymentOnlyGate />;
  }
  return <MobileAppLayout />;
}

/** Administración y Soporte solo acceden a recargas propias desde la app;
 * los paneles privilegiados siguen siendo exclusivos del portal web. */
function MobilePaymentOnlyGate() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [signingOut, setSigningOut] = useState(false);
  const signOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try { await logout(); navigate("/movil/login", { replace: true }); }
    finally { setSigningOut(false); }
  };
  return <main className="kd9-mob-admin-gate" data-testid="mobile-payment-only-gate">
    <span className="kd9-mob-admin-kicker">KD9 · BILLETERA PERSONAL</span>
    <p>Administración y Soporte pueden recargar Energía KD9 desde la app. Sus funciones administrativas siguen disponibles únicamente en la web.</p>
    {location.pathname === "/app/energia-kd" ? <div className="w-full max-w-5xl text-left"><Outlet /></div> :
      <Link className="kd9-mob-admin-web" to="/app/energia-kd" data-testid="mobile-payment-only-onepay-link">Energía KD9 · Pagar con OnePay <ArrowRight size={19} aria-hidden="true" /></Link>}
    {location.pathname === "/app/energia-kd" && <Link to="/app/inicio">Volver</Link>}
    <button type="button" className="kd9-mob-admin-logout" onClick={signOut} disabled={signingOut} data-testid="mobile-payment-only-logout">
      <LogOut size={17} aria-hidden="true" />{signingOut ? "Cerrando sesión…" : "Cerrar sesión"}
    </button>
  </main>;
}

function MobileAdminGate() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const signOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await logout();
      navigate("/movil/login", { replace: true });
    } finally {
      setSigningOut(false);
    }
  };
  return (
    <main className="kd9-mob-admin-gate" data-testid="mobile-admin-gate">
      <div className="kd9-mob-admin-emblem" aria-hidden="true"><LockKeyhole size={40}/></div>
      <span className="kd9-mob-admin-kicker">KHAOS DELTA 9 · PORTAL ADMINISTRATIVO</span>
      <h1>Acceso exclusivo desde la web</h1>
      <p>Esta función solamente está disponible para usuarios, consultores y creadores desde la aplicación móvil.</p>
      <p>Para ingresar a tu panel de Dirección, Administración o Soporte, por favor ingresa desde la versión web de KD9.</p>
      <a className="kd9-mob-admin-web" href="https://khaosdelta9.com/login"
         target="_blank" rel="noopener noreferrer" data-testid="mobile-admin-web-link">
        Ir al portal web <ArrowRight size={19} aria-hidden="true"/>
      </a>
      <button type="button" className="kd9-mob-admin-logout" onClick={signOut}
              disabled={signingOut} data-testid="mobile-admin-logout">
        <LogOut size={17} aria-hidden="true"/>
        {signingOut ? "Cerrando sesión…" : "Cerrar sesión"}
      </button>
    </main>
  );
}

function MobileAppLayout() {
  const location = useLocation();
  const { accessMap } = useAuth();
  const { role } = useRole();
  const tabs = useMemo(() => mobileTabs(role, accessMap), [role, accessMap]);
  // The app has its own dark obsidian identity; do not mutate saved web preferences.
  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains("dark");
    root.classList.add("dark");
    return () => { if (!hadDark) root.classList.remove("dark"); };
  }, []);
  // Retain existing responsive table labels in the standalone mobile shell.
  useEffect(() => {
    const main = document.getElementById("kd9-mobile-main");
    if (!main) return undefined;
    const annotate = () => {
      main.querySelectorAll("table").forEach((table) => {
        const headers = Array.from(table.querySelectorAll("thead th")).map((c) => c.textContent.trim());
        table.querySelectorAll("tbody tr").forEach((tr) => {
          Array.from(tr.children).forEach((cell, index) => {
            if (cell.tagName === "TD" && headers[index] && !cell.dataset.label) cell.dataset.label = headers[index];
          });
        });
      });
    };
    annotate();
    const observer = new MutationObserver(annotate);
    observer.observe(main, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
  // Every mobile destination starts at its top instead of inheriting the
  // previous page's scroll offset (a common problem with persistent tab bars).
  useEffect(() => {
    const scrollRegion = document.getElementById("kd9-mobile-main");
    if (scrollRegion) scrollRegion.scrollTop = 0;
  }, [location.pathname]);
  const label = role === "consultor" ? "CONSULTOR KD9" : role === "creador" ? "CREADOR KD9" : "UNIVERSO KD9";
  return (
    <div className="kd9-mob-app" data-testid="kd9-mobile-experience">
      <SessionActivityTracker />
      <header className="kd9-mob-header">
        <Link to="/app/inicio" className="kd9-mob-brand" aria-label="Ir al inicio de KD9">
          <KD9LogoCompact size={44} />
          <span className="kd9-mob-brand-label"><strong>KHAOS DELTA 9</strong><small>{label}</small></span>
        </Link>
        <div className="kd9-mob-head-actions">
          <EnergyBalanceIndicator />
          <Link className="kd9-mob-icon-button" to="/app/mensajes" aria-label="Notificaciones y mensajes"><Bell size={20}/></Link>
        </div>
      </header>

      <main className="kd9-responsive-content kd9-mob-main" id="kd9-mobile-main">
        <Outlet />
        {location.pathname === "/app/perfil" && <MobileSignOut />}
      </main>
      <nav className="kd9-mob-bottom" aria-label="Secciones principales de la app KD9" data-testid="kd9-mobile-bottom-nav">
        {tabs.map((tab) => {
          const Icon = ICONS[tab.icon] || Home;
          const active = tab.to === "/app/inicio"
            ? location.pathname === tab.to
            : tab.to === "/app/explorar-movil"
              ? location.pathname === tab.to
              : location.pathname === tab.to || location.pathname.startsWith(tab.to + "/");
          return (
            <NavLink key={tab.label} to={tab.to} className={`kd9-mob-tab ${active ? "is-active" : ""}`}
              aria-current={active ? "page" : undefined} data-testid={`kd9-mobile-tab-${tab.icon}`}>
              <Icon size={22} strokeWidth={active ? 2.5 : 1.8} aria-hidden="true" />
              <span>{tab.label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}

/** Use the shared auth context: revokes the session in the backend and clears local role state. */
function MobileSignOut() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pending, setPending] = useState(false);

  const endSession = async () => {
    if (pending) return;
    setPending(true);
    try {
      await logout();
      navigate("/movil/login", { replace: true });
    } finally {
      setPending(false);
      setConfirmOpen(false);
    }
  };

  return (
    <section className="kd9-mob-session" aria-label="Seguridad de tu cuenta" data-testid="mobile-session-controls">
      <Link to="/descarga" className="kd9-mob-app-update" data-testid="mobile-update-app-link">
        <Sparkles size={19} aria-hidden="true" />
        <span>Descargar KD9 para Android</span>
        <ArrowRight size={17} aria-hidden="true"/>
      </Link>
      <Link to="/descarga#iphone" className="kd9-mob-app-update" data-testid="mobile-install-iphone-link">
        <Smartphone size={19} aria-hidden="true" />
        <span>Descargar KD9 para iPhone</span>
        <ArrowRight size={17} aria-hidden="true"/>
      </Link>
      <h2>Seguridad de tu cuenta</h2>
      <p>Al salir, deberás volver a iniciar sesión para acceder a tus servicios KD9.</p>
      <button
        type="button"
        className="kd9-mob-logout-btn"
        onClick={() => setConfirmOpen(true)}
        data-testid="mobile-logout-btn"
      >
        <LogOut size={20} aria-hidden="true" />
        <span>Cerrar sesión</span>
      </button>

      {confirmOpen && (
        <div className="kd9-mob-confirm-backdrop" role="presentation">
          <div className="kd9-mob-confirm" role="alertdialog" aria-modal="true"
               aria-labelledby="kd9-mob-logout-title" aria-describedby="kd9-mob-logout-description">
            <h3 id="kd9-mob-logout-title">¿Cerrar sesión?</h3>
            <p id="kd9-mob-logout-description">Se cerrará tu sesión actual en KD9.</p>
            <div className="kd9-mob-confirm-actions">
              <button type="button" autoFocus disabled={pending} onClick={() => setConfirmOpen(false)}>
                Cancelar
              </button>
              <button type="button" disabled={pending} onClick={endSession}
                      data-testid="mobile-logout-confirm">
                {pending ? "Saliendo…" : "Sí, cerrar sesión"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export function MobileDashboardRouter() {
  if (!mobileModeEnabled()) return <DashboardRouter />;
  return <MobileHome />;
}

function MobileHome() {
  const { user, accessMap } = useAuth();
  const { role } = useRole();
  const { t } = useLanguage();
  const catalog = useMemo(() => mobileCatalog(role, accessMap), [role, accessMap]);
  const allowed = useMemo(() => new Set(catalog.flatMap(s => s.items.map(i => i.to))), [catalog]);
  const suggested = favorites.filter((item) => allowed.has(item.to)).slice(0, 4);
  const name = String(user?.name || "").trim().split(/\s+/)[0] || "viajero";
  const professional = role === "consultor" || role === "creador";
  const featured = professional
    ? { label: "TU ESPACIO PROFESIONAL", title: "Tus herramientas, a un toque", body: "Gestiona tus servicios, contenidos y actividades desde KD9.", to: "/app/explorar-movil", action: "Ver herramientas" }
    : { label: "UNIVERSO ANCESTRAL", title: "Tu camino comienza aquí", body: "Diagnósticos, aprendizaje, comunidad y experiencias dentro de un solo universo.", to: "/app/explorar-movil", action: "Explorar KD9" };

  return (
    <div className="kd9-mob-home" data-testid="kd9-mobile-home">
      <div className="kd9-mob-greeting"><span>BIENVENIDO A KD9</span><h1>Hola, {name}</h1><p>La energía que despierta lo ancestral.</p></div>
      <section className="kd9-mob-hero" aria-label="Descubre KD9">
        <div className="kd9-mob-hero-mark" aria-hidden="true">𓂀</div>
        <div className="kd9-mob-hero-content">
          <span>{featured.label}</span><h2>{featured.title}</h2><p>{featured.body}</p>
          <Link to={featured.to} className="kd9-mob-hero-cta">{featured.action}<ArrowRight size={18}/></Link>
        </div>
      </section>
      <div className="kd9-mob-title-line"><h2>Accesos para ti</h2><Link to="/app/explorar-movil">Ver todo <ArrowRight size={16}/></Link></div>
      {suggested.length ? <div className="kd9-mob-quick-grid">
        {suggested.map(({to, title, description, Icon}) => (
          <Link key={to} to={to} className="kd9-mob-quick-card">
            <div className="kd9-mob-quick-icon"><Icon size={23}/></div>
            <strong>{title}</strong><small>{description}</small>
          </Link>
        ))}
      </div> : <div className="kd9-mob-empty">Todavía no hay accesos destacados para tu perfil. Puedes explorar las secciones habilitadas.</div>}
      <div className="kd9-mob-title-line"><h2>Tu territorio</h2></div>
      <div className="kd9-mob-feature-links">
        {catalog.slice(0, 3).map((section, i) => (
          <Link key={section.label} to={section.items[0].to} className="kd9-mob-feature-row">
            <span className="kd9-mob-feature-number">{String(i + 1).padStart(2, "0")}</span>
            <span><strong>{section.label.includes(".") ? t(section.label) : section.label}</strong><small>{section.items.length} accesos disponibles</small></span>
            <ArrowRight size={18}/>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function MobileExplore() {
  const { accessMap } = useAuth();
  const { role } = useRole();
  const { t } = useLanguage();
  const [query, setQuery] = useState("");
  const catalog = useMemo(() => mobileCatalog(role, accessMap), [role, accessMap]);
  const filtered = catalog.map((section) => ({
    ...section,
    items: section.items.filter((item) => {
      const label = item.labelKey ? t(item.labelKey) : item.label || "";
      return (label + " " + section.label).toLocaleLowerCase("es").includes(query.trim().toLocaleLowerCase("es"));
    }),
  })).filter(s => s.items.length);
  if (!mobileModeEnabled()) return <div className="kd9-mob-explore-entry"><p>Este explorador está disponible en la experiencia móvil KD9.</p><Link to="/movil">Abrir experiencia móvil</Link></div>;
  return (
    <div className="kd9-mob-explore" data-testid="kd9-mobile-explore">
      <header className="kd9-mob-explore-heading"><span>DESCUBRIR</span><h1>Explora tu universo</h1><p>Encuentra tus herramientas y servicios habilitados.</p></header>
      <label className="kd9-mob-search">
        <Search size={20} aria-hidden="true"/>
        <span className="sr-only">Buscar servicios KD9</span>
        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Busca diagnósticos, cursos, servicios..." />
        {query && <button onClick={() => setQuery("")} aria-label="Borrar búsqueda"><X size={18}/></button>}
      </label>
      {filtered.length ? filtered.map((section) => (
        <section key={section.label} className="kd9-mob-explore-section">
          <h2>{section.label.includes(".") ? t(section.label) : section.label}</h2>
          <div className="kd9-mob-service-grid">{section.items.map((item) => {
            const Icon = item.icon || Sparkles;
            return <Link to={item.to} key={item.to} className="kd9-mob-service-card">
              <span className="kd9-mob-service-icon"><Icon size={23}/></span>
              <strong>{item.labelKey ? t(item.labelKey) : item.label}</strong>
              <ArrowRight size={15} className="kd9-mob-service-arrow"/>
            </Link>;
          })}</div>
        </section>
      )) : <div className="kd9-mob-empty">No se encontraron servicios habilitados con ese nombre.</div>}
    </div>
  );
}
